import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';
import useMergedRef from 'hooks/useMergedRef';

import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';

import Button from './Button';
import StringInput from './StringInput';
import * as S from './styled/IntegerInput.styled';

/**
 * @author Burak Unsal
 * @description Integer input component
 * @param {object} props
 * @param {number} props.min - Minimum value
 * @param {number} props.max - Maximum value
 * @param {Object} props.unit - Unit of the input
 * @param {string} props.name - name of the input
 * @param {string} props.errorMessage - error message
 * @param {string} props.placeholder - placeholder text
 * @param {string} props.maxPlaceholder - maximum value of the input
 * @param {"right" | "left"} [props.unitPosition = 'right'] - position of the unit
 * @param {boolean} props.disabled	// if true, input will be disabled
 * @param {"view" | "edit" | "error"} props.viewMode - view mode of the input
 * @param {boolean} [props.showArrows = true] - show arrow buttons
 * @param {boolean} [props.allowFloat = false] - allow float numbers
 * @param {boolean} [props.allowNegativeValues = false] - allow negative values
 * @param {number} [props.decimalLimit] - decimal character limit of the integer value
 * @param {number} [props.limit] - character limit of the integer value
 */
const IntegerInput = (
	{
		unit,
		unitPosition = 'right',
		showArrows = true,
		allowFloat = false,
		allowNegativeValues = false,
		min,
		max,
		decimalLimit,
		limit,
		disabled,
		viewMode,
		maxPlaceholder,
		step = 1,
		...restProps
	},
	ref
) => {
	const [componentRef, setComponentRef] = useState(null);
	const refs = useMergedRef(ref, setComponentRef);

	const { t } = useTranslation();
	const { UnitSystem } = useLocale();
	const selectedUnit = UnitSystem[unit] ?? unit;
	const realUnit = selectedUnit?.name ?? selectedUnit;

	const InputContainerPadding = realUnit ? t(realUnit).length * 0.6 + 2.4 + (selectedUnit?.name ? 0.6 : 0) : 1.2;

	const handleKeyPress = (e) => {
		const val = e.target.value;
		const isKeyDot = e.key === '.';
		const isKeyComma = e.key === ',';
		const isKeyMinus = e.key === '-';
		const isContainsDot = val.includes('.');
		const isContainsComma = val.includes(',');
		const isContainsMinus = val.includes('-');
		const cursorPosition = e.target.selectionStart;
		const cursorEndPosition = e.target.selectionEnd;
		const isNotSelection = cursorPosition === cursorEndPosition;

		if (allowFloat) {
			if ((isContainsComma || isContainsDot) && (isKeyComma || isKeyDot)) {
				e.preventDefault();
				return;
			}

			if (isKeyComma) {
				componentRef.value = `${val},`;
				e.preventDefault();
				return;
			}

			if (isKeyDot) {
				componentRef.value = `${val}.`;
				e.preventDefault();
				return;
			}
		}
		if (allowNegativeValues && isKeyMinus && isContainsMinus) {
			e.preventDefault();
			return;
		}

		const calculatedLimit = limit ?? (allowFloat ? 10 : 9);

		if (decimalLimit) {
			const separator = isContainsDot ? '.' : isContainsComma ? ',' : null;
			if (separator) {
				const decimalIndex = val.indexOf(separator);
				const decimal = val.split(separator)[1];
				if (
					decimal.length >= decimalLimit &&
					(isNotSelection ? decimalIndex < cursorPosition : cursorPosition > decimalIndex)
				) {
					e.preventDefault();
					return;
				}
			}
		}

		if (val.length >= calculatedLimit) {
			e.preventDefault();
			return;
		}
		const regex = new RegExp(`^[0-9]${allowFloat ? '|\\.|\\,' : ''}${allowNegativeValues ? '|\\-' : ''}`);
		const preventKey = !regex.test(e.key);
		preventKey && e.preventDefault();
	};

	const handleMinusButtonClick = () => {
		const componentValue = componentRef.value;
		const convertedValue = componentValue.replace(',', '.');
		const val = isNaN(convertedValue) ? 0 : Number(convertedValue);
		const newVal = Number(Math.max(allowNegativeValues ? min : 0, val - parseFloat(step)).toFixed(10));
		const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
		nativeInputValueSetter.call(componentRef, newVal);

		const inputEvent = new Event('input', { bubbles: true });
		componentRef.dispatchEvent(inputEvent);
	};

	const handlePlusButtonClick = () => {
		const componentValue = componentRef.value;
		const convertedValue = componentValue.replace(',', '.');
		const val = isNaN(convertedValue) ? 0 : Number(convertedValue);
		const newVal = Number(
			Math.min(max !== undefined ? max : Number.MAX_SAFE_INTEGER, (val + parseFloat(step)).toFixed(10))
		);
		const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
		nativeInputValueSetter.call(componentRef, newVal);

		const inputEvent = new Event('input', { bubbles: true });
		componentRef.dispatchEvent(inputEvent);
	};

	return (
		<S.Container className="integer-input-container" showArrows={showArrows}>
			{showArrows && !disabled && !viewMode && (
				<Button
					variant="blackOutline"
					icon={faMinus}
					onClick={handleMinusButtonClick}
					className="minus-button"
				/>
			)}
			<S.InputContainer
				unitPosition={unitPosition}
				showArrows={showArrows}
				inputContainerPadding={InputContainerPadding}
				viewMode={viewMode}>
				<StringInput
					ref={refs}
					min={allowNegativeValues ? min : 0}
					onKeyPress={handleKeyPress}
					viewMode={viewMode}
					disabled={disabled}
					forceNumber
					{...restProps}
				/>
				{maxPlaceholder !== undefined && (
					<div className="max-placeholder">
						<Typography fontWeight="700" color="var(--content-3)">
							/ {maxPlaceholder}
						</Typography>
					</div>
				)}
				{selectedUnit && (
					<S.UnitContainer unitPosition={unitPosition} viewMode={viewMode}>
						<Typography fontWeight="700" color="var(--content-3)">
							{typeof selectedUnit === 'string' ? (
								t(selectedUnit)
							) : selectedUnit.sup ? (
								<>
									{t(selectedUnit.name)}
									<sup>{selectedUnit.sup}</sup>
								</>
							) : (
								t(selectedUnit.name)
							)}
						</Typography>
					</S.UnitContainer>
				)}
			</S.InputContainer>
			{showArrows && !disabled && !viewMode && (
				<Button variant="blackOutline" icon={faPlus} onClick={handlePlusButtonClick} className="plus-button" />
			)}
		</S.Container>
	);
};

export default forwardRef(IntegerInput);
